import {
  UTILITY_DEFAULT_ICONS,
  pickSiteSocialLinksFromContent,
  readUtilityItems,
  type LayoutBlock,
  type SiteSocialNetworkKey,
  type UtilityItemRow,
} from '@web-studio/shared-schemas';
import { SITE_SOCIAL_ICONS } from '../icons/socialNetworks';
import { pickBlockField } from './localized';
import { safeMailtoHref, safeTelHref } from './safeGuestLinks';

export type ResolvedUtilityItem =
  | {
      id: string;
      kind: 'phone';
      icon: string;
      label: string;
      value: string;
      href: string;
    }
  | {
      id: string;
      kind: 'email';
      icon: string;
      label: string;
      value: string;
      href: string;
    }
  | {
      id: string;
      kind: 'address';
      icon: string;
      label: string;
      value: string;
      href?: undefined;
    }
  | {
      id: string;
      kind: 'social';
      network: SiteSocialNetworkKey;
      label: string;
      value: string;
      href: string;
    };

function isPresent(value: string): boolean {
  return Boolean(value) && value !== '—';
}

function resolveContact(
  row: UtilityItemRow,
  block: LayoutBlock,
  locale: string,
): ResolvedUtilityItem | null {
  if (row.kind === 'phone') {
    const value = pickBlockField(block.content, 'phone', locale).trim();
    const href = safeTelHref(value);
    if (!isPresent(value) || !href) return null;
    return {
      id: row.id,
      kind: 'phone',
      icon: row.icon?.trim() || UTILITY_DEFAULT_ICONS.phone,
      label: row.label?.trim() || value,
      value,
      href,
    };
  }

  if (row.kind === 'email') {
    const value = pickBlockField(block.content, 'email', locale).trim();
    const href = safeMailtoHref(value);
    if (!isPresent(value) || !href) return null;
    return {
      id: row.id,
      kind: 'email',
      icon: row.icon?.trim() || UTILITY_DEFAULT_ICONS.email,
      label: row.label?.trim() || value,
      value,
      href,
    };
  }

  if (row.kind === 'address') {
    const value = pickBlockField(block.content, 'address', locale).trim();
    if (!isPresent(value)) return null;
    return {
      id: row.id,
      kind: 'address',
      icon: row.icon?.trim() || UTILITY_DEFAULT_ICONS.address,
      label: row.label?.trim() || value,
      value,
    };
  }

  return null;
}

/** One Social entry → every hydrated / custom social URL on the block. */
function resolveSocialLinks(
  rowId: string,
  block: LayoutBlock,
  locale: string,
): ResolvedUtilityItem[] {
  return pickSiteSocialLinksFromContent(block.content, locale).map((link) => ({
    id: `${rowId}-${link.network}`,
    kind: 'social' as const,
    network: link.network,
    label: SITE_SOCIAL_ICONS[link.network].title,
    value: link.url,
    href: link.url,
  }));
}

/** Resolve configured Utility Bar rows against hydrated PDL content. Empty values omit. */
export function resolveUtilityBarItems(
  block: LayoutBlock,
  locale: string,
): ResolvedUtilityItem[] {
  const rows = readUtilityItems(pickBlockField(block.content, 'utilityItems', locale));
  const out: ResolvedUtilityItem[] = [];
  let socialExpanded = false;
  for (const row of rows) {
    if (row.kind === 'social') {
      if (socialExpanded) continue;
      socialExpanded = true;
      out.push(...resolveSocialLinks(row.id, block, locale));
      continue;
    }
    const resolved = resolveContact(row, block, locale);
    if (resolved) out.push(resolved);
  }
  return out;
}
